import logger from '@eventbrite/client-logger';
import {
    Button,
    STYLE_FILL,
    STYLE_REBRANDING_FILL,
} from '@eventbrite/eds-button';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeElements, StripeError } from '@stripe/stripe-js';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { getStripePaymentTypeName } from './getStripePaymentTypeName';
import styles from './StripeButton.module.scss';
import { useHandleRedirectedPayment } from './useHandleRedirectedPayment';

export type StripeButtonHandleErrorCallback = (
    errorMsg: GenericLazyString | string,
) => Promise<void>;

export interface StripeButtonProps {
    hasErrors: boolean;
    firstName: string;
    lastName: string;
    email: string;
    formData: Record<string, any>;
    submitFormCb?: (
        token: { confirmationTokenID: string },
        formData: Record<string, any>,
        stripeRedirectedPaymentCallback: (
            paymentIntentClientSecret: string,
            handleErrorCallback: StripeButtonHandleErrorCallback,
        ) => Promise<void>,
    ) => void;
    stripePaymentType: string | null;
    isAppRebrandActive?: boolean;
}

export const StripeButton: React.FC<StripeButtonProps> = ({
    hasErrors,
    firstName,
    lastName,
    email,
    formData,
    submitFormCb,
    stripePaymentType,
    isAppRebrandActive = false,
}: StripeButtonProps) => {
    const [isLoading, setIsLoading] = useState(false);

    const stripe = useStripe();
    const elements: StripeElements | null = useElements();
    const stripeRedirectedPaymentCallback = useHandleRedirectedPayment(stripe);
    const stripeIsLoaded = stripe && elements;
    const isButtonDisabled = isLoading || !stripeIsLoaded;

    const handleError = (error?: StripeError) => {
        logger.error(`Stripe error ${error}`);
        setIsLoading(false);
    };

    useEffect(() => {
        if (hasErrors) {
            setIsLoading(false);
        }
    }, [hasErrors]);

    const handleSubmit = async () => {
        if (!submitFormCb) {
            logger.info('No submit callback provided');
            return;
        }

        if (!stripeIsLoaded) {
            logger.info(
                'Stripe is not yet loaded when "pay" button is clicked',
            );
            return;
        }

        setIsLoading(true);

        const { error: submitError } = await elements.submit();
        if (submitError) {
            handleError(submitError);
            return;
        }

        const { error: paymentDataError, confirmationToken } =
            await stripe.createConfirmationToken({
                elements,
                params: {
                    payment_method_data: {
                        billing_details: {
                            name: `${firstName} ${lastName}`,
                            email,
                        },
                    },
                },
            });

        if (paymentDataError) {
            handleError(paymentDataError);
            return;
        }

        submitFormCb(
            {
                confirmationTokenID: confirmationToken.id,
            },
            formData,
            stripeRedirectedPaymentCallback,
        );
    };

    const getButtonText = () => {
        const paymentTypeName = getStripePaymentTypeName(stripePaymentType);
        if (paymentTypeName) {
            return gettext('Place Order With %(provider)s', {
                provider: paymentTypeName,
            });
        }

        return gettext('Place Order');
    };

    // TODO Consider switching to Marmalade's Button and LoadingButton:
    // https://www.marmaladedesignsystem.com/76b74a45e/p/39b980-button/b/390e08
    // https://www.marmaladedesignsystem.com/76b74a45e/v/0/p/0496c4-loading-button/b/822c66
    // Problem - LoadingButton seems to look different from the old button with isLoading=true
    return (
        <div
            className={classNames({
                'eds-align--left': true,
            })}
        >
            <Button
                onClick={handleSubmit}
                __containerClassName={classNames({
                    'modal-footer-scrollable-on-desktop': true,
                })}
                style={classNames(
                    isAppRebrandActive ? STYLE_REBRANDING_FILL : STYLE_FILL,
                    styles.padded,
                )}
                disabled={isButtonDisabled}
                keepDisabledStyleWhileLoading={false}
                data-spec="eds-modal__primary-button"
                data-automation="eds-modal__primary-button"
                data-testid="eds-modal__primary-button"
                isLoading={isLoading}
                isFullWidth={true}
            >
                {getButtonText()}
            </Button>
        </div>
    );
};
